// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack API Hooks — Git Integration
// apps/web/src/lib/api/git.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { ApiError } from './types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface GitRepository {
  id: number;
  projectId: number;
  platform: string;
  owner: string;
  repoName: string;
  fullName: string;
  cloneUrl: string;
  defaultBranch: string;
  isConnected: boolean;
  lastSyncDate: string | null;
  syncStatus: 'Pending' | 'Syncing' | 'Synced' | 'Failed';
  syncError: string | null;
  totalCommits: number;
  hasWebhook: boolean;
  createdDate: string;
  projectName?: string;
}

export interface GitCommit {
  id: number;
  gitRepositoryId: number;
  sessionId: number | null;
  workItemId: number | null;
  commitHash: string;
  shortHash: string;
  authorName: string;
  authorEmail: string;
  authorDate: string;
  message: string;
  branchName: string | null;
  linesAdded: number;
  linesRemoved: number;
  filesChanged: number;
  isMergeCommit: boolean;
  workItemName: string | null;
  repositoryFullName: string | null;
}

export interface SyncResult {
  repositoryId: number;
  commitsSynced: number;
  commitsSkipped: number;
  workItemsLinked: number;
  syncedAt: string;
  error: string | null;
}

// ─── Generic fetch ──────────────────────────────────────────────────────────

function useApiFetch<T>(path: string, enabled = true) {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    try { setLoading(true); setError(null); const api = await createApi(); const res = await api.get<T>(path); setData(res.data); }
    catch (err: any) { setError({ message: err.response?.data?.message || err.message, status: err.response?.status || 500 }); }
    finally { setLoading(false); }
  }, [createApi, path]);

  useEffect(() => { if (enabled) fetch(); }, [fetch, enabled]);
  return { data, loading, error, refetch: fetch };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useGitRepositories(projectId?: number) {
  const path = projectId ? `/api/v1/git/repositories?projectId=${projectId}` : '/api/v1/git/repositories';
  return useApiFetch<GitRepository[]>(path);
}

export function useGitCommits(repositoryId: number, workItemId?: number) {
  const params = new URLSearchParams({ repositoryId: String(repositoryId) });
  if (workItemId) params.set('workItemId', String(workItemId));
  return useApiFetch<GitCommit[]>(`/api/v1/git/commits?${params}`, repositoryId > 0);
}

// ─── GitHub App Hooks ──────────────────────────────────────────────────────

export interface GitHubInstallation {
  id: number;
  gitHubInstallationId: number;
  accountLogin: string;
  accountType: string;
  avatarUrl: string | null;
  isActive: boolean;
  installedAt: string;
  repoCount: number;
}

export interface GitHubAppRepo {
  id: number;
  fullName: string;
  defaultBranch: string;
  isPrivate: boolean;
  description: string | null;
}

export function useGitHubInstallations() {
  return useApiFetch<GitHubInstallation[]>('/api/v1/github/installations');
}

export function useGitHubInstallUrl() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openInstall = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const res = await api.get<{ installUrl: string }>('/api/v1/github/install');
      window.open(res.data.installUrl, '_blank');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to get install URL. GitHub App may not be configured.';
      setError(msg);
      console.error('Failed to get install URL', err);
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  return { openInstall, loading, error };
}

export function useGitHubAppRepos(installationId: number) {
  return useApiFetch<GitHubAppRepo[]>(
    `/api/v1/github/repos?installationId=${installationId}`,
    installationId > 0
  );
}

export function useGitMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const connect = useCallback(async (data: { projectId: number; repositoryUrl: string; setupWebhook?: boolean }): Promise<GitRepository | null> => {
    try { setLoading(true); setError(null); const api = await createApi(); const res = await api.post<GitRepository>('/api/v1/git/repositories/connect', data); return res.data; }
    catch (err: any) { setError({ message: err.response?.data?.message || 'Failed to connect', status: err.response?.status || 500 }); return null; }
    finally { setLoading(false); }
  }, [createApi]);

  const disconnect = useCallback(async (id: number): Promise<boolean> => {
    try { setLoading(true); const api = await createApi(); await api.post(`/api/v1/git/repositories/${id}/disconnect`); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  const sync = useCallback(async (repositoryId: number, since?: string): Promise<SyncResult | null> => {
    try { setLoading(true); setError(null); const api = await createApi(); const res = await api.post<SyncResult>('/api/v1/git/sync', { repositoryId, since }); return res.data; }
    catch (err: any) { setError({ message: err.response?.data?.message || 'Sync failed', status: err.response?.status || 500 }); return null; }
    finally { setLoading(false); }
  }, [createApi]);

  const linkCommit = useCallback(async (commitId: number, workItemId?: number, sessionId?: number): Promise<GitCommit | null> => {
    try { setLoading(true); const api = await createApi(); const res = await api.put<GitCommit>('/api/v1/git/commits/link', { commitId, workItemId, sessionId }); return res.data; }
    catch { return null; } finally { setLoading(false); }
  }, [createApi]);

  return { connect, disconnect, sync, linkCommit, loading, error };
}

// ─── GitHub OAuth Hooks ────────────────────────────────────────────────────

export interface GitHubOAuthStatus {
  connected: boolean;
  login: string | null;
  avatarUrl: string | null;
  connectedAt: string | null;
}

export interface GitHubOAuthRepo {
  fullName: string;
  defaultBranch: string;
  isPrivate: boolean;
  description: string | null;
}

export function useGitHubOAuthStatus() {
  return useApiFetch<GitHubOAuthStatus>('/api/v1/github/oauth/status');
}

export function useGitHubOAuthRepos(enabled: boolean) {
  return useApiFetch<GitHubOAuthRepo[]>('/api/v1/github/oauth/repos', enabled);
}

export function useGitHubOAuth() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorize = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const res = await api.get<{ authorizeUrl: string }>('/api/v1/github/oauth/authorize');
      window.location.href = res.data.authorizeUrl;
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to start GitHub authorization.');
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  const disconnect = useCallback(async () => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.delete('/api/v1/github/oauth');
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  return { authorize, disconnect, loading, error };
}
